/*
 * home-table-datasource.ts
 * dev-utms-shop-admin
 * 
 * Last Modified: 04.06.2025
 * Modified By: Tetiana Makovii
 * 
 * Created by Tetiana Makovii on 04.07.2023
 * Copyright © 2026 Sonova AG. All rights reserved.
 */



import { DataSource } from '@angular/cdk/collections';
import { Observable, BehaviorSubject, Subscription, first, skip } from 'rxjs';
import { SortingDirectionsEnum } from 'src/common-panel/enums/sorting-directions-enum';
import { PatientManagerListService } from 'src/patient-manager/services/patient-manager-list.service';
import { SortingHeadersEnum } from './enums/sorting-headers-enum';
import { SortingKeysEnum } from './enums/sorting-keys-enum';
import { PMPatientListItem } from 'src/patient-manager/common/models/internal/pm-patient-list-item.model';
import { ShopFilterStates } from './enums/shop-filter-states';
import { PMShopItemJSON } from 'src/patient-manager/common/models/server-response/pm-shop-item-json.model';
import { CountryId } from 'src/common-panel/constants/country-configs.constants';



export class PatientManagerTableDataSource extends DataSource<PMPatientListItem>
{
    subscription: Subscription = new Subscription()

    private _data: PMPatientListItem[] = []
    private _sortDirection: SortingDirectionsEnum
    private _sortingHeader: SortingHeadersEnum

    private _countryFilterState: CountryId | undefined

    private _shopFilterState = this.patientManagerListService.tableSettings.shopFilterState;
    private _currentFilterShop = this.patientManagerListService.tableSettings.currentFilterShop;

    private _pageSize: number
    private _pageIndex: number

    private patientsSubject = new BehaviorSubject<PMPatientListItem[]>([]);
    private loadingSubject = new BehaviorSubject<boolean>(false)

    public loading$ = this.loadingSubject.asObservable()

    get count()
    {
        return this._data.length
    }

    get data()
    {
        return this._data
    }

    set sortDirection(value: SortingDirectionsEnum)
    {
        this._sortDirection = value
        this.patientManagerListService.tableSettings.sortDirection = value
    }

    get sortDirection(): SortingDirectionsEnum
    {
        return this._sortDirection
    }

    set sortingHeader(value: SortingHeadersEnum)
    {
        this._sortingHeader = value
        this.patientManagerListService.tableSettings.sortingHeader = value
    }

    get sortingHeader(): SortingHeadersEnum
    {
        return this._sortingHeader
    }

    set countryFilter(state: CountryId | undefined)
    {
        this._countryFilterState = state
        this.patientManagerListService.tableSettings.countryFilterState = state
    }

    get countryFilter(): CountryId | undefined
    {
        return this._countryFilterState
    }

    set shopFilter(value: { state: ShopFilterStates, shop?: PMShopItemJSON })
    {
        this._shopFilterState = value.state
        this._currentFilterShop = value.shop
        this.patientManagerListService.tableSettings.shopFilterState = value.state
        this.patientManagerListService.tableSettings.currentFilterShop = value.shop
    }

    get shopFilter(): { state: ShopFilterStates, shop?: PMShopItemJSON }
    {
        return { state: this._shopFilterState, shop: this._currentFilterShop }
    }

    set pageSize(value: number)
    {
        this._pageSize = value
        this.patientManagerListService.tableSettings.pageSize = value
    }

    get pageSize(): number
    {
        return this._pageSize
    }

    set pageIndex(value: number)
    {
        this._pageIndex = value
        this.patientManagerListService.tableSettings.pageIndex = value
    }

    get pageIndex(): number
    {
        return this._pageIndex
    }

    constructor(private patientManagerListService: PatientManagerListService)
    {
        super();
        this._sortDirection = this.patientManagerListService.tableSettings.sortDirection;
        this._sortingHeader = this.patientManagerListService.tableSettings.sortingHeader;
        this._pageSize = this.patientManagerListService.tableSettings.pageSize;
        this._pageIndex = this.patientManagerListService.tableSettings.pageIndex;

        this._countryFilterState = this.patientManagerListService.tableSettings.countryFilterState;
    }


    /**
     * Connect this data source to the table. The table will only update when
     * the returned stream emits new items.
     * @returns A stream of the items to be rendered.
     */

    connect(): Observable<PMPatientListItem[]>
    {
        return this.patientsSubject.asObservable();
    }

    /**
     *  Called when the table is being destroyed. Use this function, to clean up
     * any open connections or free any held resources that were set up during connect.
     */

    disconnect()
    {
        this.patientsSubject.complete();
        this.loadingSubject.complete();
        this.subscription.unsubscribe()
    }


    loadPatients()
    {
        this.patientsSubject.next([])
        this.subscription.unsubscribe()
        this.subscription=new Subscription()
        if (this.patientManagerListService.filterByCountry.value)
        {
            this.loadingSubject.next(true)
            let l_country = this.patientManagerListService.filterByCountry.value;



            this.patientManagerListService.loadADPatientsData(l_country);
            this.patientManagerListService.loadShopsData(l_country);
            this.patientManagerListService.loadPendingDeletionPatients();

            let l_sub = this.patientManagerListService.loadFirstPartData(l_country).pipe(first(val => val == true)).subscribe(() =>
            {
                this.loadingSubject.next(false)
                this._data = this.patientManagerListService.data.slice()
                this.filterData()
                if (this.patientManagerListService.filterByIdEmailOrName.value.length > 0)
                {
                    this._data = this._data.filter(l_patient => l_patient.email?.toLowerCase().startsWith(this.patientManagerListService.filterByIdEmailOrName.value.toLowerCase())
                        || l_patient.patientId?.toLowerCase().startsWith(this.patientManagerListService.filterByIdEmailOrName.value.toLowerCase())
                        || l_patient.firstName?.toLowerCase().startsWith(this.patientManagerListService.filterByIdEmailOrName.value.toLowerCase())
                        || l_patient.lastName?.toLowerCase().startsWith(this.patientManagerListService.filterByIdEmailOrName.value.toLowerCase()))

                }
                if (this.sortDirection != SortingDirectionsEnum.none) this.sortData();
                this.loadPatientsPage();
                let l_sub2 = this.patientManagerListService.loadRemainingPartData(l_country).pipe(first(val => val == true)).subscribe(() =>
                {
                    this._data = this.patientManagerListService.data.slice()
                    this.filterData();
                    if (this.patientManagerListService.filterByIdEmailOrName.value.length > 0)
                    {
                        this._data = this._data.filter(l_patient => l_patient.email?.toLowerCase().startsWith(this.patientManagerListService.filterByIdEmailOrName.value.toLowerCase())
                            || l_patient.patientId?.toLowerCase().startsWith(this.patientManagerListService.filterByIdEmailOrName.value.toLowerCase())
                            || l_patient.firstName?.toLowerCase().startsWith(this.patientManagerListService.filterByIdEmailOrName.value.toLowerCase())
                            || l_patient.lastName?.toLowerCase().startsWith(this.patientManagerListService.filterByIdEmailOrName.value.toLowerCase()))

                    }
                    if (this.sortDirection != SortingDirectionsEnum.none) this.sortData();
                    this.loadPatientsPage();
                })
                this.subscription.add(l_sub2)
            })
            this.subscription.add(l_sub)


        }

        let l_sub1 = this.patientManagerListService.filterByIdEmailOrName.pipe(skip(1)).subscribe(l_input =>
        {
            this._data = this.patientManagerListService.data;
            if (l_input.length > 0)
            {
                this._data = this._data.filter(l_patient => l_patient.email?.toLowerCase().startsWith(l_input.toLowerCase())
                    || l_patient.patientId?.toLowerCase().startsWith(l_input.toLowerCase())
                    || l_patient.firstName?.toLowerCase().startsWith(l_input.toLowerCase())
                    || l_patient.lastName?.toLowerCase().startsWith(l_input.toLowerCase()))
            }
            this.pageIndex = 0;
            this.loadPatientsPage();
        })

        this.subscription.add(l_sub1)
    }


    loadPatientsPage()
    {
        let l_result = this._data.slice(this.pageIndex * this.pageSize, (this.pageIndex + 1) * this.pageSize)
        this.patientsSubject.next(l_result)

    }


    sortData()
    {
        if (this.sortDirection == SortingDirectionsEnum.none)
        {
            this._data = this._data.sort(() => Math.random() - 0.5)
        }
        else
        {
            switch (this.sortingHeader)
            {
                case SortingHeadersEnum.name:
                    {
                        this.simpleSort(SortingKeysEnum.lastName);
                        break;
                    }
                case SortingHeadersEnum.email:
                    {
                        this.emailSort();
                        break;
                    }
                case SortingHeadersEnum.patientId:
                    {
                        this.simpleSort(SortingKeysEnum.patientId);
                        break;
                    }
                case SortingHeadersEnum.shopId:
                    {
                        this.simpleSort(SortingKeysEnum.shopId);
                        break;
                    }
                case SortingHeadersEnum.lastSeen:
                    {
                        this.sortByDate(SortingKeysEnum.lastSeen);
                        break;
                    }
                case SortingHeadersEnum.dateOfRegistration:
                    {
                        this.sortByDate(SortingKeysEnum.dateOfRegistration,);
                        break;
                    }



            }
        }
    }

    sortByDate(key: string)
    {
        let l_returnTrue = 1
        let l_returnFalse = -1

        if (this.sortDirection == SortingDirectionsEnum.desc)
        {
            l_returnTrue = -1
            l_returnFalse = 1
        }

        this._data.sort((obj1, obj2) =>
        {
            if (Object.keys(obj1).includes(key))
            {

                if (!(obj1 as any)[key] || (obj1 as any)[key] == "–")
                {
                    return 1
                }

                if (!(obj2 as any)[key] || (obj2 as any)[key] == "–")
                {
                    return -1
                }

                if ((obj1 as any)[key] > (obj2 as any)[key])
                {
                    return l_returnFalse;
                }

                if ((obj1 as any)[key] < (obj2 as any)[key])
                {
                    return l_returnTrue;
                }
            } else
            {
                throw new Error(`Key ${ key } does not found in keys of object of type PatientListItem`)
            }
            return 0;
        });
    }

    simpleSort(key: string, isNumber: boolean = false)
    {
        let l_returnTrue = 1
        let l_returnFalse = -1

        if (this.sortDirection == SortingDirectionsEnum.desc)
        {
            l_returnTrue = -1
            l_returnFalse = 1
        }

        this._data.sort((obj1, obj2) =>
        {
            if (Object.keys(obj1).includes(key))
            {
                if (!(obj1 as any)[key] || (obj1 as any)[key] == "–")
                {
                    return 1
                }

                if (!(obj2 as any)[key] || (obj2 as any)[key] == "–")
                {
                    return -1
                }

                if ((obj1 as any)[key] > (obj2 as any)[key])
                {
                    return l_returnTrue;
                }

                if ((obj1 as any)[key] < (obj2 as any)[key])
                {
                    return l_returnFalse;
                }
            } else
            {
                throw new Error(`Key ${ key } does not found in keys of object of type PMPatientListItem`)
            }
            return 0;
        });
    }

    emailSort()
    {
        let l_returnTrue = 1
        let l_returnFalse = -1
        if (this.sortDirection == SortingDirectionsEnum.desc)
        {
            l_returnTrue = -1
            l_returnFalse = 1
        }

        this._data.sort((obj1, obj2) =>
        {

            if (!obj1.email ||obj1.email == "–")
            {
                return 1
            }

            if (!obj2.email ||obj2.email == "–")
            {
                return -1
            }

            let l_emailObj1 = obj1.email.split('@')[0]
            let l_emailObj2 = obj2.email.split('@')[0]
            if (l_emailObj1.toLowerCase() > l_emailObj2.toLowerCase())
            {
                return l_returnTrue;
            }

            if (l_emailObj1.toLowerCase() < l_emailObj2.toLowerCase())
            {
                return l_returnFalse;
            }

            return 0;
        });
    }

    filterData()
    {
        this._data = this.patientManagerListService.data;

        switch (this.shopFilter.state)
        {

            case ShopFilterStates.oneShop:
                {
                    this._data = this._data.filter(item => item.shopId.toLowerCase() == this._currentFilterShop?.shopId.toLowerCase())
                    break;
                }
            case ShopFilterStates.all:
                {
                    break;
                }
        }

    }
}
